/**
 * 全社PLの事業部行を開いたときに出す階層の内訳。
 * 事業部 → 拠点 → 事業所 → … と辿り、各段で「取引を見る」から根拠(個別取引)を確認できる。
 */
import { useEffect, useState } from 'react';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { formatYen } from '@core/lib/money';
import { toShortDate } from '@core/lib/date';
import { fetchHierarchyNodes } from '@core/masters/hierarchyRepo';
import { fetchBookedTransactions } from './plRepo';
import {
  aggregateByNode,
  childrenOf,
  transactionsUnassigned,
  transactionsUnder,
  type DrilldownResult,
} from './drilldown';
import type {
  Account,
  Department,
  HierarchyNode,
  MonthStr,
  PlLine,
  Transaction,
} from '@core/lib/types';

type EvidenceTarget = { label: string; transactions: Transaction[] };

export default function DepartmentDrilldown({
  department,
  month,
  plLine,
  accounts,
  accountName,
  userNames,
}: {
  department: Department;
  month: MonthStr;
  plLine: PlLine;
  accounts: Account[];
  accountName: (id: string) => string;
  userNames: Map<string, string>;
}) {
  const toast = useToast();
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [result, setResult] = useState<DrilldownResult | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [evidence, setEvidence] = useState<EvidenceTarget | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [hierarchy, transactions] = await Promise.all([
          fetchHierarchyNodes(department.id).catch(() => [] as HierarchyNode[]),
          fetchBookedTransactions(department.id, month),
        ]);
        if (!alive) return;
        setNodes(hierarchy);
        setResult(aggregateByNode(transactions, hierarchy, accounts, plLine));
      } catch (e) {
        if (alive) toast(e instanceof Error ? e.message : '内訳を取得できませんでした');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [department.id, month, plLine, accounts, toast]);

  if (loading) {
    return (
      <tr>
        <td colSpan={6} className="hint" style={{ paddingLeft: 52 }}>
          内訳を読み込み中…
        </td>
      </tr>
    );
  }

  if (!result) return null;

  const hasHierarchy = nodes.length > 0;
  const unassignedTx = transactionsUnassigned(result.transactions);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderNodes(parentId: string | null, depth: number): React.ReactNode[] {
    return childrenOf(nodes, parentId).flatMap((node) => {
      const amount = result!.byNode.get(node.id)?.total ?? 0;
      if (amount === 0) return [];
      const kids = childrenOf(nodes, node.id).filter(
        (k) => (result!.byNode.get(k.id)?.total ?? 0) > 0,
      );
      const isOpen = open.has(node.id);

      const row = (
        <tr key={`${department.id}-${node.id}`}>
          <td>
            <div className="pl-name" style={{ paddingLeft: 52 + depth * 20 }}>
              {kids.length > 0 ? (
                <button
                  className="txn-link"
                  style={{ textDecoration: 'none', padding: 0 }}
                  onClick={() => toggle(node.id)}
                >
                  <span className={`chev${isOpen ? ' open' : ''}`}>▶</span>
                </button>
              ) : (
                <span className="chev" />
              )}
              <span className="lvl">{node.level_label || '—'}</span>
              {node.name}
              <button
                className="txn-link"
                onClick={() =>
                  setEvidence({
                    label: `${department.name} › ${node.name}`,
                    transactions: transactionsUnder(node.id, result!.transactions, nodes),
                  })
                }
              >
                取引を見る
              </button>
            </div>
          </td>
          <td className="num hint">—</td>
          <td className="num">{formatYen(amount)}</td>
          <td className="num hint">—</td>
          <td className="num hint">—</td>
          <td />
        </tr>
      );

      return isOpen ? [row, ...renderNodes(node.id, depth + 1)] : [row];
    });
  }

  return (
    <>
      {hasHierarchy && renderNodes(null, 0)}

      {result.unassigned > 0 && (
        <tr>
          <td>
            <div className="pl-name" style={{ paddingLeft: 52 }}>
              <span className="chev" />
              <span className="hint">(階層未設定)</span>
              <button
                className="txn-link"
                onClick={() =>
                  setEvidence({
                    label: `${department.name} › (階層未設定)`,
                    transactions: unassignedTx,
                  })
                }
              >
                取引を見る
              </button>
            </div>
          </td>
          <td className="num hint">—</td>
          <td className="num">{formatYen(result.unassigned)}</td>
          <td className="num hint">—</td>
          <td className="num hint">—</td>
          <td />
        </tr>
      )}

      {evidence && (
        <tr>
          <td colSpan={6} style={{ padding: 0, border: 'none' }}>
            <Modal onClose={() => setEvidence(null)} width="min(760px, 92vw)">
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3>取引一覧(根拠)</h3>
                  <div className="path" style={{ margin: '2px 0 0' }}>
                    {evidence.label} / {evidence.transactions.length}件
                  </div>
                </div>
              </div>
              <div style={{ overflow: 'auto' }}>
                {evidence.transactions.length === 0 ? (
                  <p className="empty">該当する取引はありません。</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>計上日</th>
                        <th>科目</th>
                        <th>摘要</th>
                        <th className="num">金額</th>
                        <th>入力者</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evidence.transactions.map((t) => (
                        <tr key={t.id}>
                          <td>{toShortDate(t.posting_date)}</td>
                          <td>{accountName(t.account_id)}</td>
                          <td>{t.description ?? ''}</td>
                          <td className="num">{formatYen(t.amount)}</td>
                          <td className="hint">
                            {t.source === 'routine'
                              ? 'Routine自動'
                              : (userNames.get(t.created_by) ?? '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Modal>
          </td>
        </tr>
      )}
    </>
  );
}
